import { useCallback, useEffect, useState } from 'react'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { getStaticConfigFile, saveStaticConfigFile } from '../services/configService'
import type { StaticCssFileBlockProps } from '../types'

/**
 * Mirrors Angular `SaveStyleCSSComponent` — textarea + Save for `/api/config/static/:filename`.
 */
export function StaticCssFileBlock({ filename }: StaticCssFileBlockProps) {
  const fieldId = `static-css-${filename.replace(/[^a-zA-Z0-9_-]/g, '-')}`
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const text = await getStaticConfigFile(filename)
      setContent(text)
    } catch (error) {
      showApiError(error)
      setContent('')
    } finally {
      setLoading(false)
    }
  }, [filename])

  useEffect(() => {
    void load()
  }, [load])

  const handleSave = async () => {
    setSaving(true)
    try {
      await saveStaticConfigFile(filename, content)
      showSuccess('Style Change Successfully!')
    } catch (error) {
      showApiError(error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <ModuleScreenLoading minHeight="120px" className="py-8" />
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
      <label className="block text-sm font-medium text-slate-700" htmlFor={fieldId}>
        {filename}
      </label>
      <textarea
        id={fieldId}
        rows={10}
        value={content}
        onChange={(event) => setContent(event.target.value)}
        className="w-full font-mono text-xs px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        spellCheck={false}
      />
      <button
        type="button"
        onClick={() => void handleSave()}
        disabled={saving}
        className={primaryEntityActionButtonClass}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  )
}
